/**
 * Engine calculations region — transformation × calculation cell per
 * scope §6.5. Atlas Day 3a P1.
 *
 * Single-region-with-internal-sections rendering per scope §4.1 cluster
 * principle (single-region-with-internal-sections mode). 2 internal
 * sections per scope §6.5:
 *   - Primary calculations: calculate_profitability + calculate_profitability_tiers
 *     + calculate_breakdown_extras (present-state profitability)
 *   - Forward-looking projections: forecast_restock + calculate_po_impact
 *
 * Cluster-style header + "Together:" line per scope §4.5 cluster
 * Together: line altitude principle. Click handler navigates to inspect
 * view per Atlas Day 2.5 P1 URL routing pattern.
 *
 * No substrate fetch — engine RPCs are code-altitude not data-altitude;
 * region surfaces engine architecture without per-value counts.
 */

import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator } from 'lucide-react';

export default function EngineCalculationsRegion(): JSX.Element {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      data-sub-region-id="engine-calculations"
      onClick={() => navigate('/admin/engine-atlas/inspect/engine-calculations')}
      className="w-full text-left border border-border bg-surface-1 rounded-lg p-4 hover:border-accent transition-colors cursor-pointer space-y-3"
    >
      <div className="flex items-start gap-2">
        <Calculator size={16} className="text-ink-secondary flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h2 className="text-sm font-semibold text-ink-bright">Engine calculations</h2>
          <p className="text-xs text-ink-secondary mt-1">
            The math the engine performs.
          </p>
        </div>
      </div>

      {/* COMPOSABILITY: per-section "Last changed X days ago" indicator slot — wires post-Day 92 */}

      {/* 2 internal sections per scope §6.5 */}
      <div className="space-y-2 pl-6">
        <div>
          <h3 className="text-xs font-semibold text-ink-bright">Primary calculations</h3>
          <p className="text-xs text-ink-secondary mt-0.5">
            Present-state profitability across single-quantity and tiered modes + breakdown
            inspection (calculate_profitability + calculate_profitability_tiers +
            calculate_breakdown_extras).
          </p>
        </div>
        <div>
          <h3 className="text-xs font-semibold text-ink-bright">Forward-looking projections</h3>
          <p className="text-xs text-ink-secondary mt-0.5">
            Future-state restock + PO impact projections (forecast_restock +
            calculate_po_impact).
          </p>
        </div>
      </div>

      <p className="text-xs text-ink-secondary pl-6">
        <span className="font-semibold text-ink-bright">Together:</span> the engine&apos;s
        present-state and forward-looking outputs.
      </p>
    </button>
  );
}
